import { NoteConflict } from './NoteConflict';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { NoteEditor } from './NoteEditor';
import { newNoteRequestId, type NoteDraft, type NoteMention } from './types';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
export function NoteComposer({
  draftKey,
  _customer,
  _teamConversation,
  _business,
  initialText = '',
  initialMentions = [],
  expectedRevision,
  onSave,
  onCancel,
}: {
  draftKey: string;
  _customer?: string;
  _teamConversation?: string;
  _business: string;
  initialText?: string;
  initialMentions?: NoteMention[];
  expectedRevision?: number;
  onSave: (draft: NoteDraft) => Promise<any>;
  onCancel?: () => void;
}) {
  const user = useSelector((state: any) => state.auth?.user?._id);
  // Phones: Return adds a line (no Shift key on a touch keyboard); Save saves.
  const isMobile = useIsMobile();
  const storageKey = `note-draft:${user}:${draftKey}`;
  const [draft, setDraft] = useState<NoteDraft>(() => {
    try {
      const cached = sessionStorage.getItem(storageKey);
      if (cached) return JSON.parse(cached);
    } catch {
      /* storage may be disabled */
    }
    return {
      text: initialText,
      mentions: initialMentions,
      requestId: newNoteRequestId(),
      expectedRevision,
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState<any>(null);
  const inFlight = useRef(false);
  const save = async () => {
    if (inFlight.current || conflict || !draft.text.trim()) return;
    inFlight.current = true;
    setSaving(true);
    setError('');
    try {
      const result = await onSave(draft);
      if (result?.error)
        throw Object.assign(
          new Error(result.message || 'Could not save your note. Your draft is safe.'),
          { latestNote: result.latestNote },
        );
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* optional storage */
      }
      setDraft({ text: '', mentions: [], requestId: newNoteRequestId(), expectedRevision });
    } catch (error: any) {
      setConflict(error.latestNote || error.response?.data?.latestNote || null);
      setError(error.response?.data?.message || error.message || 'Could not save your note.');
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };
  return (
    <div className="space-y-2 p-2">
      <NoteConflict
        note={conflict}
        onReviewed={() => {
          setDraft({
            ...draft,
            expectedRevision: conflict.noteRevision,
            requestId: newNoteRequestId(),
          });
          setConflict(null);
          setError('');
        }}
      />
      <NoteEditor
        value={draft.text}
        mentions={draft.mentions}
        _customer={_customer}
        _teamConversation={_teamConversation}
        _business={_business}
        disabled={saving}
        // When the keyboard opens, the editor scrolls into view with Save under it.
        editorClassName="max-md:scroll-mb-20 touch:scroll-mb-20"
        submitOnEnter={!isMobile}
        onSubmit={save}
        onChange={(text, mentions) => {
          const updated = {
            text,
            mentions,
            requestId: newNoteRequestId(),
            expectedRevision: draft.expectedRevision,
          };
          setDraft(updated);
          try {
            sessionStorage.setItem(storageKey, JSON.stringify(updated));
          } catch {
            /* keep draft in memory */
          }
        }}
      />
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex items-center justify-between gap-2 max-md:flex-wrap">
        <span className="text-xs text-muted-foreground max-md:min-w-0 max-md:flex-1 max-md:basis-40">
          {_teamConversation
            ? 'Only group members can see this message'
            : 'Only your team can see this note'}
        </span>
        <div className="flex gap-2 max-md:ml-auto">
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          )}
          <Button
            size="sm"
            disabled={
              saving || Boolean(conflict) || !draft.text.trim() || draft.text.length > 10000
            }
            onClick={save}
          >
            {saving ? 'Saving…' : _teamConversation ? 'Save message' : 'Save note'}
          </Button>
        </div>
      </div>
    </div>
  );
}
